import { Head, Link as InertiaLink } from "@inertiajs/react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Link,
  Separator,
  Text,
} from "@radix-ui/themes";
import Breadcrumb from "@/shared/Breadcrumb";
import {
  AtSign,
  Camera,
  ExternalLink,
  Facebook,
  Image as ImageIcon,
  Instagram,
  MapPin,
  Music2,
  Youtube,
} from "lucide-react";
import {
  MediaGroupShowEvent,
  MediaGroupShowMediaGroup,
  MediaGroupShowParticipant,
} from "@/types/shared";

interface Props {
  media_group: MediaGroupShowMediaGroup;
  participant: MediaGroupShowParticipant;
  attended_events: MediaGroupShowEvent[];
  upcoming_events: MediaGroupShowEvent[];
}

const EVENT_GRID_COLS = "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3";

export default function MediaGroupShow({
  media_group,
  participant,
  attended_events,
  upcoming_events,
}: Props) {
  const hasAnyContact =
    participant.instagram ||
    participant.youtube ||
    participant.flickr ||
    participant.facebook ||
    participant.tiktok ||
    participant.threads;

  return (
    <>
      <Head title={participant.name} />
      <div className="flex flex-col px-4 pt-18 lg:px-48">
        <Box mb="3">
          <Breadcrumb
            crumbs={[
              { label: "Media Groups" },
              { label: participant.name },
            ]}
          />
        </Box>
        {/* Header */}
        <Flex justify="between" align="start" gap="4" mb="4">
          <Flex align="center" gap="4">
            {media_group.logo_url && (
              <img
                src={media_group.logo_url}
                alt={`${participant.name} logo`}
                style={{
                  width: 72,
                  height: 72,
                  objectFit: "cover",
                  borderRadius: 12,
                  flexShrink: 0,
                }}
              />
            )}
            <Box>
              <Flex align="center" gap="3" mb="2" wrap="wrap">
                <Heading as="h1" size={{ initial: "7", md: "8" }}>
                  {participant.name}
                </Heading>
                {participant.established && (
                  <Badge variant="soft" size="2">
                    Est. {participant.established}
                  </Badge>
                )}
              </Flex>
              <Flex gap="3" align="center" wrap="wrap">
                {participant.location && (
                  <Flex align="center" gap="1">
                    <MapPin size={14} className="text-gray9" />
                    <Text size="2" color="gray">
                      {participant.location}
                    </Text>
                  </Flex>
                )}
                {media_group.primary_outlet && (
                  <Flex align="center" gap="1">
                    <ImageIcon size={14} className="text-gray9" />
                    <Text size="2" color="gray">
                      Primary Outlet &mdash; {media_group.primary_outlet}
                    </Text>
                  </Flex>
                )}
              </Flex>
            </Box>
          </Flex>

          {media_group.portfolio_link && (
            <Button asChild variant="outline" color="gray" size="2">
              <a
                href={media_group.portfolio_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink size={14} />
                Website
              </a>
            </Button>
          )}
        </Flex>

        <Separator size="4" my="4" />

        {/* Contact */}
        <Box mb="8">
          <Heading as="h2" size="5" mb="4">
            Contact
          </Heading>
          {hasAnyContact ? (
            <Flex direction="column" gap="3">
              {participant.instagram && (
                <SocialRow
                  icon={<Instagram size={16} className="text-gray9 shrink-0" />}
                  href={instagramHref(participant.instagram)}
                  label={`@${stripLeadingAt(participant.instagram)}`}
                />
              )}
              {participant.youtube && (
                <SocialRow
                  icon={<Youtube size={16} className="text-gray9 shrink-0" />}
                  href={participant.youtube}
                  label={youtubeLabel(participant.youtube)}
                />
              )}
              {participant.flickr && (
                <SocialRow
                  icon={<Camera size={16} className="text-gray9 shrink-0" />}
                  href={participant.flickr}
                  label={flickrLabel(participant.flickr)}
                />
              )}
              {participant.facebook && (
                <SocialRow
                  icon={<Facebook size={16} className="text-gray9 shrink-0" />}
                  href={facebookHref(participant.facebook)}
                  label={facebookLabel(participant.facebook)}
                />
              )}
              {participant.tiktok && (
                <SocialRow
                  icon={<Music2 size={16} className="text-gray9 shrink-0" />}
                  href={`https://www.tiktok.com/@${stripLeadingAt(participant.tiktok)}`}
                  label={`@${stripLeadingAt(participant.tiktok)}`}
                />
              )}
              {participant.threads && (
                <SocialRow
                  icon={<AtSign size={16} className="text-gray9 shrink-0" />}
                  href={`https://www.threads.net/@${stripLeadingAt(participant.threads)}`}
                  label={`@${stripLeadingAt(participant.threads)}`}
                />
              )}
            </Flex>
          ) : (
            <Text size="2" color="gray">
              No contact info listed.
            </Text>
          )}
        </Box>

        {/* Upcoming events */}
        {upcoming_events.length > 0 && (
          <Box mb="8">
            <Heading as="h2" size="5" mb="4">
              Upcoming Events
            </Heading>
            <div className={EVENT_GRID_COLS}>
              {upcoming_events.map((event) => (
                <EventRow key={event.id} event={event} />
              ))}
            </div>
          </Box>
        )}

        {/* Attended Events */}
        <Box mb="8">
          <Heading as="h2" size="5" mb="4">
            Events Attended
          </Heading>
          {attended_events.length === 0 ? (
            <Text size="2" color="gray">
              No attended events on record yet.
            </Text>
          ) : (
            <div className={EVENT_GRID_COLS}>
              {attended_events.map((event) => (
                <EventRow key={event.id} event={event} />
              ))}
            </div>
          )}
        </Box>

        {/* Notable events (free text) */}
        {participant.notable_events && (
          <Box mb="8">
            <Heading as="h2" size="5" mb="2">
              Notable Events
            </Heading>
            <Text
              size="2"
              color="gray"
              as="p"
              style={{ whiteSpace: "pre-line" }}
            >
              {participant.notable_events}
            </Text>
          </Box>
        )}
      </div>
    </>
  );
}

function SocialRow({
  icon,
  href,
  label,
}: {
  icon: React.ReactNode;
  href: string;
  label: string;
}) {
  return (
    <Flex align="center" gap="2">
      {icon}
      <Link href={href} target="_blank" rel="noopener noreferrer" size="2">
        {label}
      </Link>
    </Flex>
  );
}

function EventRow({ event }: { event: MediaGroupShowEvent }) {
  return (
    <InertiaLink
      href={`/events/${event.id}`}
      className="block border-t-2 border-t-accent9 rounded-lg p-4 shadow-sm hover:shadow-md transition no-underline text-inherit"
    >
      <Text size="3" weight="medium" as="p">
        {event.name}
      </Text>
      <Flex justify="between" align="center" mt="1">
        <Link
          href={`/tracks/${event.track_slug}`}
          size="2"
          onClick={(e) => e.stopPropagation()}
        >
          {event.track_name}
        </Link>
        <Text size="1" color="gray">
          {event.date}
        </Text>
      </Flex>
    </InertiaLink>
  );
}

function stripLeadingAt(value: string): string {
  return value.replace(/^@+/, "");
}

function instagramHref(handle: string): string {
  return `https://instagram.com/${stripLeadingAt(handle)}`;
}

function parseUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function youtubeLabel(value: string): string {
  const u = parseUrl(value);
  if (!u) return value;
  const path = u.pathname.replace(/^\/+|\/+$/g, "");
  if (path.startsWith("@")) return path.split("/")[0];
  const match = path.match(/^(?:c|user|channel)\/([^/]+)/);
  if (match) return match[1];
  return u.hostname.replace(/^www\./, "");
}

function flickrLabel(value: string): string {
  const u = parseUrl(value);
  if (!u) return value;
  const match = u.pathname.match(/^\/(?:photos|people)\/([^/]+)/);
  if (match) return `@${match[1]}`;
  return u.hostname.replace(/^www\./, "");
}

function facebookHref(value: string): string {
  if (/^https?:\/\//i.test(value)) return value;
  return `https://www.facebook.com/${stripLeadingAt(value)}`;
}

function facebookLabel(value: string): string {
  const u = parseUrl(value);
  if (u) {
    const path = u.pathname.replace(/^\/+|\/+$/g, "").split("/")[0];
    return path || u.hostname.replace(/^www\./, "");
  }
  return stripLeadingAt(value);
}
